import { ButtonType, BUTTON_SELECTOR } from '@ardoq/button';
import {
  ReactElement,
  PropsWithChildren,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { colors, s4 } from '@ardoq/design-tokens';
import styled, { css } from 'styled-components';
import { TabProps, TabsGroupProps } from './types';
import { hideScrollbars } from '@ardoq/style-helpers';
import { tabPropsToDropdownOption } from './TabsPropsToDropdownOption';
import { ChevronTabButton } from './TabButton';
import { calculateTabsOrder } from './calculateTabsOrder';
import { measureTabWidths } from './utils';
import {
  ButtonWithDropdown,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { take, takeRight } from 'lodash';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { isTruthy } from '@ardoq/common-helpers';
import { useChevronSliders } from '@ardoq/hooks';

const Wrapper = styled.div<{
  $align?: AlignSetting;
}>`
  overflow: hidden;
  position: relative;
  display: flex;
  background: ${colors.surfaceDefault};
  justify-content: ${({ $align }) => $align};
  width: 100%;

  /* Needed in order to keep the "More" button the same height
     as the tabs buttons */
  align-items: stretch;
  ${BUTTON_SELECTOR} {
    margin: ${s4} 0;
    /* Cover the case where all tabs are collapsed and only
       "More" dropdown button is visible
       setting explicit height to that button prevents the bar height drop */
    height: 48px;
  }
`;

export const TabsButtonChildrenWrapper = styled.div<{
  $fullButtons?: boolean;
  $isReady?: boolean;
}>`
  display: flex;
  overflow-x: auto;
  gap: ${s4};
  padding: ${s4};
  ${hideScrollbars};

  ${({ $fullButtons }) =>
    $fullButtons &&
    css`
      flex: 1;
    `};

  ${({ $isReady }) =>
    $isReady === false &&
    css`
      visibility: hidden;
    `};
`;

type TabsButtonWrapperProps = Pick<
  TabsGroupProps,
  | 'activeTabId'
  | 'onTabChange'
  | 'extraDropdownOptions'
  | 'tablistAriaLabel'
  | 'fullButtons'
  | 'moreButtonLabel'
> & {
  rootWrapperWidth: number;
  children: ReactElement<PropsWithChildren<TabProps>>[];
  align?: AlignSetting;
  clickNamespace?: string;
};

export const TabsButtonWrapperWithChevrons = ({
  children,
  align,
  rootWrapperWidth,
  fullButtons,
  tablistAriaLabel,
}: PropsWithChildren<TabsButtonWrapperProps>) => {
  const {
    wrapperRef,
    wrapperOnScroll,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick,
    onRightChevronClick,
  } = useChevronSliders(rootWrapperWidth);

  return (
    <Wrapper $align={align}>
      <ChevronTabButton
        isVisible={isLeftChevronVisible}
        direction="left"
        onClick={onLeftChevronClick}
      />
      <TabsButtonChildrenWrapper
        $fullButtons={fullButtons}
        role="tablist"
        ref={wrapperRef}
        onScroll={wrapperOnScroll}
        aria-label={tablistAriaLabel}
      >
        {children}
      </TabsButtonChildrenWrapper>
      <ChevronTabButton
        isVisible={isRightChevronVisible}
        direction="right"
        onClick={onRightChevronClick}
      />
    </Wrapper>
  );
};

// Fallback width used to reserve space for the "More" dropdown button until
// it has rendered and we can measure the actual width via ref. Sized for the
// English "More" default; once measured, the real `offsetWidth` (which can
// be larger for longer translated labels) is used.
const DROPDOWN_MORE_BUTTON_WIDTH_FALLBACK = 130;

type RenderProps = {
  childrenToShowInTheBar: ReactElement<PropsWithChildren<TabProps>>[];
  moreOptions: DropdownOption[];
  isReady: boolean;
};

const calculateTabDistribution = (
  tabWidths: Record<string, { tabId: string; width: number }>,
  activeTabId: string,
  availableWidth: number
) => {
  const { indexOfLastTabFittingToWrapper, sortedTabs } = calculateTabsOrder(
    `${activeTabId}`,
    Object.values(tabWidths),
    availableWidth
  );

  return {
    sortedTabIds: sortedTabs.map(tab => tab.tabId),
    numberOfTabsFitting: indexOfLastTabFittingToWrapper + 1,
  };
};

const splitChildrenByDistribution = (
  children: ReactElement<PropsWithChildren<TabProps>>[],
  sortedTabIds: string[],
  numberOfTabsFitting: number,
  onTabChange: ((tabId: string) => void) | undefined,
  activeTabId: string | number | undefined
) => {
  const childrenLookup = Object.fromEntries(
    children.map(child => [child.props.tabId, child])
  );

  const sortedChildren = sortedTabIds.map(tabId => childrenLookup[tabId]);

  const childrenForBar = take(sortedChildren, numberOfTabsFitting);
  const childrenInDropdown = takeRight(
    sortedChildren,
    sortedChildren.length - numberOfTabsFitting
  );

  const childrenForDropdown: DropdownOption[] = childrenInDropdown.map(
    ({ props }) =>
      tabPropsToDropdownOption(props, {
        onTabChange,
        activeTabId: `${activeTabId}`,
      })
  );

  return { childrenForBar, childrenForDropdown };
};

const combineDropdownOptions = (
  childrenOptions: DropdownOption[],
  extraOptions: DropdownOption[]
): DropdownOption[] => {
  const needsDivider = childrenOptions.length && extraOptions.length;
  const divider: DropdownOption | null = needsDivider
    ? { type: DropdownOptionType.DIVIDER, label: '' }
    : null;
  return [...childrenOptions, divider, ...extraOptions].filter(isTruthy);
};

const getRenderProps = (
  tabWidths: Record<string, { tabId: string; width: number }> | null,
  children: ReactElement<PropsWithChildren<TabProps>>[],
  rootWrapperWidth: number,
  activeTabId: string | number | undefined,
  onTabChange: ((tabId: string) => void) | undefined,
  extraDropdownOptions: DropdownOption[],
  moreButtonWidth: number
): RenderProps => {
  if (tabWidths === null) {
    return {
      childrenToShowInTheBar: children,
      moreOptions: [],
      isReady: false,
    };
  }

  const availableWidth = rootWrapperWidth - moreButtonWidth;
  const { sortedTabIds, numberOfTabsFitting } = calculateTabDistribution(
    tabWidths,
    `${activeTabId}`,
    availableWidth
  );

  const { childrenForBar, childrenForDropdown } = splitChildrenByDistribution(
    children,
    sortedTabIds,
    numberOfTabsFitting,
    onTabChange,
    activeTabId
  );

  const moreOptions = combineDropdownOptions(
    childrenForDropdown,
    extraDropdownOptions
  );

  return {
    childrenToShowInTheBar: childrenForBar,
    moreOptions,
    isReady: true,
  };
};

const haveTabsChanged = (oldTabs: string[], newTabs: string[]) =>
  oldTabs.sort().join(',') !== newTabs.sort().join(',');

export const TabsButtonWrapperWithDropdown = ({
  children,
  onTabChange,
  align,
  rootWrapperWidth,
  activeTabId,
  extraDropdownOptions = [],
  clickNamespace,
  fullButtons,
  moreButtonLabel = 'More',
}: TabsButtonWrapperProps) => {
  const childrenWrapperRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const [tabWidths, setTabWidths] = useState<Record<
    string,
    { tabId: string; width: number }
  > | null>(null);
  const [moreButtonWidth, setMoreButtonWidth] = useState(
    DROPDOWN_MORE_BUTTON_WIDTH_FALLBACK
  );

  const needsMeasure = haveTabsChanged(
    tabWidths ? Object.keys(tabWidths) : [''],
    children.map(child => child.props.tabId)
  );

  useLayoutEffect(() => {
    if (!childrenWrapperRef.current) return;
    if (!needsMeasure) return;

    const measured = measureTabWidths(childrenWrapperRef.current);
    setTabWidths(measured);
  }, [needsMeasure]);

  // Measure the rendered More button so the available-width reservation
  // tracks the actual translated label rather than the English-sized fallback.
  useLayoutEffect(() => {
    const measured = moreButtonRef.current?.offsetWidth;
    if (measured && measured !== moreButtonWidth) {
      setMoreButtonWidth(measured);
    }
  }, [moreButtonLabel, moreButtonWidth]);

  const { isReady, childrenToShowInTheBar, moreOptions } = getRenderProps(
    tabWidths,
    children,
    rootWrapperWidth,
    activeTabId,
    onTabChange,
    extraDropdownOptions,
    moreButtonWidth
  );

  return (
    <Wrapper $align={align} role="tablist">
      <TabsButtonChildrenWrapper
        $fullButtons={fullButtons}
        $isReady={isReady}
        ref={childrenWrapperRef}
      >
        {isReady ? childrenToShowInTheBar : children}
      </TabsButtonChildrenWrapper>
      {isReady && Boolean(moreOptions.length) && (
        <div ref={moreButtonRef}>
          <ButtonWithDropdown
            options={moreOptions}
            alignMenuTo="right"
            dropdownSize={DropdownSize.M}
            clickNamespace={clickNamespace}
            buttonType={ButtonType.GHOST}
          >
            {moreButtonLabel}
          </ButtonWithDropdown>
        </div>
      )}
    </Wrapper>
  );
};
